import { type ComponentPropsWithoutRef } from 'react'
import { clsx } from 'clsx'
import { CheckIcon } from '../../icons/CheckIcon'
import { MinusIcon } from '../../icons/MinusIcon'
import { Text, TextProps } from '../Text/Text'
import { TitleDataCell } from './ComparisonTable.TitleDataCell'
import { tokens } from '../../theme/tokens.css'
import { visuallyHidden } from '../../utils/visibility.css'
import { activeCell, activeHeader, aligner, cell, coveredText, header, row, table } from './ComparisonTable.css'

function Root({ className, children, caption, ...props }: ComponentPropsWithoutRef<'table'> & { caption?: string }) {
  return (
    <table className={clsx(table, className)} {...props}>
      {caption && <caption className={visuallyHidden}>{caption}</caption>}
      {children}
    </table>
  )
}

function Head(props: ComponentPropsWithoutRef<'thead'>) {
  return <thead {...props} />
}

function Body(props: ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} />
}

function Row({ className, ...props }: ComponentPropsWithoutRef<'tr'>) {
  return <tr className={clsx(row, className)} {...props} />
}

type HeaderProps = Omit<ComponentPropsWithoutRef<'th'>, 'align'> & {
  alignment?: 'left' | 'center' | 'right'
  size?: TextProps['size']
  active?: boolean
  featured?: boolean
}

function Header({
  children,
  className,
  active,
  featured,
  alignment = 'center',
  size = { _: 'xs', md: 'md' },
  ...props
}: HeaderProps) {
  return (
    <th scope="col" className={clsx(header, active && activeHeader, className)} {...props}>
      {children && (
        <Text as="span" className={aligner[alignment]} size={size} color={featured ? 'textPrimary' : 'textSecondary'}>
          {children}
        </Text>
      )}
    </th>
  )
}

type DataCellProps = ComponentPropsWithoutRef<'td'> & {
  alignment?: 'left' | 'center' | 'right'
  active?: boolean
}

function DataCell({ className, children, active, alignment = 'center', ...props }: DataCellProps) {
  return (
    <td className={clsx(cell, active && activeCell, className)} {...props}>
      <Text as="span" className={clsx(coveredText, aligner[alignment])} size={{ _: 'xs', md: 'md' }}>
        {children}
      </Text>
    </td>
  )
}

function CoveredIcon(props: ComponentPropsWithoutRef<typeof CheckIcon>) {
  return <CheckIcon size="1.125rem" {...props} />
}

function NotCoveredIcon(props: ComponentPropsWithoutRef<typeof MinusIcon>) {
  return <MinusIcon size="1.125rem" color={tokens.legacyColors.gray500} {...props} />
}

export const ComparisonTable = {
  Root,
  Head,
  Body,
  Row,
  Header,
  TitleDataCell,
  DataCell,
  CoveredIcon,
  NotCoveredIcon,
}
